/**
 * A native image pane for photo evidence (passport scans, screenshots).
 *
 * LegalWork previews images as images (`preview.tsx`, `image` kind). CaseBlink
 * used to show the PDF rendition instead, which blurs a photo and hides its
 * real size. The rendition still exists — the packet assembles from it and
 * "Annotate as PDF" opens the PDF editor on it — but reading a picture should
 * mean seeing the picture. Fit / actual-size toggle, zoom with the wheel while
 * holding ⌘/Ctrl, and drag to pan.
 */
import { Button } from "@/components/ui/button";
import { Maximize, Minus, Plus, Scan } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

type Props = {
  file: Blob;
  name: string;
  ariaLabel?: string;
  /** Host controls rendered at the right of the toolbar. */
  controlsEndSlot?: ReactNode;
};

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];

export const ImagePreview: FC<Props> = ({
  file,
  name,
  ariaLabel,
  controlsEndSlot
}) => {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [fit, setFit] = useState(true);
  const [zoom, setZoom] = useState(1);
  const viewport = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    x: number;
    y: number;
    left: number;
    top: number;
  } | null>(null);

  const step = useCallback((direction: 1 | -1) => {
    setFit(false);
    setZoom((current) => {
      const index = ZOOM_STEPS.findIndex((value) => value >= current - 1e-6);
      const next =
        ZOOM_STEPS[
          Math.min(
            ZOOM_STEPS.length - 1,
            Math.max(0, (index < 0 ? ZOOM_STEPS.length - 1 : index) + direction)
          )
        ];
      return next ?? current;
    });
  }, []);

  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      step(event.deltaY < 0 ? 1 : -1);
    },
    [step]
  );

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    const element = viewport.current;
    if (!element || event.button !== 0) return;
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      left: element.scrollLeft,
      top: element.scrollTop
    };
    element.setPointerCapture(event.pointerId);
  }, []);
  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const element = viewport.current;
    if (!element || !drag.current) return;
    element.scrollLeft = drag.current.left - (event.clientX - drag.current.x);
    element.scrollTop = drag.current.top - (event.clientY - drag.current.y);
  }, []);
  const onPointerUp = useCallback((event: React.PointerEvent) => {
    drag.current = null;
    viewport.current?.releasePointerCapture(event.pointerId);
  }, []);

  const dimensions = natural ? `${natural.w} × ${natural.h}` : "";

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-fill-1"
      aria-label={ariaLabel ?? name}
      data-testid="image-preview"
    >
      <div className="flex h-9 shrink-0 items-center gap-1 border-b bg-background px-2">
        <span className="truncate text-xs text-muted-foreground" title={name}>
          {dimensions}
        </span>
        <div className="ml-auto flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Zoom out"
            onClick={() => step(-1)}
          >
            <Minus />
          </Button>
          <span className="w-10 text-center text-xs tabular-nums text-muted-foreground">
            {fit ? "Fit" : `${Math.round(zoom * 100)}%`}
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Zoom in"
            onClick={() => step(1)}
          >
            <Plus />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={fit ? "Actual size" : "Fit to pane"}
            title={fit ? "Actual size" : "Fit to pane"}
            onClick={() => {
              if (fit) {
                setFit(false);
                setZoom(1);
              } else setFit(true);
            }}
          >
            {fit ? <Scan /> : <Maximize />}
          </Button>
          {controlsEndSlot}
        </div>
      </div>
      <div
        ref={viewport}
        className={`min-h-0 flex-1 overflow-auto ${
          fit ? "flex items-center justify-center p-4" : "cursor-grab p-4"
        }`}
        onWheel={onWheel}
        onPointerDown={fit ? undefined : onPointerDown}
        onPointerMove={fit ? undefined : onPointerMove}
        onPointerUp={fit ? undefined : onPointerUp}
        onPointerCancel={fit ? undefined : onPointerUp}
      >
        <img
          src={url}
          alt={name}
          draggable={false}
          onLoad={(event) =>
            setNatural({
              w: event.currentTarget.naturalWidth,
              h: event.currentTarget.naturalHeight
            })
          }
          className={
            fit ? "max-h-full max-w-full object-contain shadow-sm" : "shadow-sm"
          }
          style={
            fit || !natural
              ? undefined
              : {
                  width: natural.w * zoom,
                  height: natural.h * zoom,
                  maxWidth: "none"
                }
          }
        />
      </div>
    </div>
  );
};
